/**
 * WEB-1695 — ПОЛОЖЕНИЕ БЛОКА ПО ГОРИЗОНТАЛИ: влево · по центру · вправо.
 *
 * Репорты владельца 24.08 подряд: «Галочка» («сейчас только слева и всё»),
 * «Иконка + текст», список с маркерами, нумерованный список, «Карточка»,
 * «Отзывы», «Звёзды оценки» («позиционирование работает только для звёзд, а
 * текст остаётся на месте»).
 *
 * КОРЕНЬ. Настройка `align` в конструкторе была, но показывалась СТРОГО пяти
 * текстовым типам, а список правился поштучно уже дважды (WEB-665 → WEB-807).
 * В самих ветках отрисовки нетекстовых блоков горизонтального положения не было
 * вовсе: `display:flex` там стоит с `alignItems:'center'` — это выравнивание
 * значка относительно текста ПО ВЫСОТЕ, а не положение блока.
 *
 * ПОЭТОМУ РАБОТА ДВОЙНАЯ, И ОБЕ ПОЛОВИНЫ ЖИВУТ ЗДЕСЬ: одна точка правды
 * «кому положено выравнивание» (её читает панель) и одна обёртка, которая его
 * ПРИМЕНЯЕТ (её зовут оба рендерера в общей точке, ветки блоков не трогаются).
 * Добавить тип в список, не научив рендер, значило бы завести новую мёртвую
 * настройку — ровно тот класс, который разбирается в WEB-1677.
 *
 * ДВА РЕЖИМА, А НЕ ОДИН — карточка требует решить это явно, а не «как
 * получилось»:
 *  • `'block'` — двигается БЛОК ЦЕЛИКОМ. Обёртка становится flex-строкой, и
 *    содержимое блока в ней сжимается по содержанию и едет к нужному краю. Так
 *    просил владелец для галочки, значка с текстом, списков и значков: они
 *    заметно уже колонки, и «прижат влево» видно глазом.
 *  • `'content'` — двигается СОДЕРЖИМОЕ ВНУТРИ блока, сам блок остаётся во всю
 *    ширину. Обёртка ставит только `text-align`. Так просил владелец для
 *    «Карточки» («добавить опцию позиционирования контента») и «Отзывов»; так
 *    же чинится и «Звёзды оценки»: ряд звёзд двигает своё поле `starsAlign`, а
 *    подпись над ним про это не знала — теперь она едет вместе с ними.
 * Сжимать «Карточку» во flex-строке было бы НЕ тем, что просили: она занимает
 * колонку целиком, и внутри неё лежат картинка, заголовок и описание.
 *
 * ⛔ ЖИВЫЕ ВОРОНКИ НЕ ТРОГАЕМ. Обёртка появляется, ТОЛЬКО когда автор задал
 * выравнивание. Блок без значения рендерится ровно прежней разметкой — без
 * лишнего узла, байт в байт. Умолчание = сегодняшнее положение (влево).
 */
import React, { type CSSProperties } from 'react';

export type BlockAlignValue = 'left' | 'center' | 'right';
export type BlockAlignMode = 'block' | 'content';

/**
 * Типы, чья СОБСТВЕННАЯ ветка отрисовки уже применяет `align` (текстовые блоки
 * WEB-665 + условия оплаты WEB-807). Общая обёртка их не трогает: два слоя на
 * одно свойство — необъявленное старшинство, тот же дефект с другой стороны.
 */
export const ALIGN_SELF_APPLIED_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'title',
  'sub_title',
  'heading',
  'paragraph',
  'payment_terms',
]);

/**
 * Кому обёртка применяет выравнивание и КАКИМ способом. Один перечень на
 * панель и на рендер — «показали, но не применили» из него не получится.
 */
export const BLOCK_ALIGN_MODES: Readonly<Record<string, BlockAlignMode>> = {
  // Строка «отметка + подпись»: у обеих `display:flex` c `alignItems:'center'`
  // (это высота), горизонтального положения не было.
  checkbox: 'block',
  terms_checkbox: 'block',
  // WEB-2158: две таких же строки «отметка + подпись».
  sensitive_consent: 'block',
  // Строка «значок + текст» — устроена ровно так же (репорт со скриншотом).
  icon_with_text: 'block',
  // Списки: двигается СПИСОК ЦЕЛИКОМ, иначе маркеры оторвались бы от строк.
  // Зашитый отступ под маркеры остаётся внутри сжавшейся коробки и едет вместе
  // с ней — список не встаёт криво.
  bullet_list: 'block',
  numbered_list: 'block',
  // Значки и чипы: заведомо уже колонки, «прижат влево» видно глазом.
  rating_chip: 'block',
  stock_badge: 'block',
  guarantee_badge: 'block',
  promo_ticket: 'block',
  review_stars: 'block',
  trust_badges: 'block',
  // WEB-1829: «Плашки-фичи» — двигается СЕТКА ЦЕЛИКОМ. Режим 'block', как у
  // соседей по семье чипов и значков: сетка бывает уже колонки (одна колонка,
  // две короткие плашки), и «прижата влево» тогда видно глазом. Своего
  // textAlign ветка отрисовки не ставит — два слоя на одно свойство были бы
  // необъявленным старшинством (шрам WEB-1677).
  feature_chips: 'block',
  // Блоки во всю ширину — двигается содержимое внутри них.
  // WEB-1822: «Цена по частям» — две строки текста во всю ширину колонки,
  // двигать нужно сам текст. Своего textAlign ветка отрисовки НЕ ставит —
  // иначе два слоя на одно свойство (шрам WEB-1677).
  price_breakdown_text: 'content',
  card: 'content',
  testimonials: 'content',
  rating_stars: 'content',
};

/**
 * У этих типов выравнивание УЖЕ есть своим контролом, и общий им не нужен —
 * два переключателя на одно свойство хуже одного (шрам WEB-1677 про два поля с
 * необъявленным старшинством):
 *  • «Обложка» — свой `align` и своя «Выравнивание текста» в панели;
 *  • «Звёзды оценки» — своё поле `starsAlign` и своя «Выравнивание звёзд».
 *    Репорт владельца был не «нет настройки», а «работает только для звёзд, а
 *    текст остаётся на месте»: общая обёртка читает ТО ЖЕ значение `starsAlign`
 *    и двигает подпись вместе с рядом звёзд. Один контрол, прежнее поле,
 *    миграции не нужны.
 */
export const ALIGN_OWN_CONTROL_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'hero',
  'rating_stars',
]);

/** Кому панель предлагает ОБЩИЙ переключатель «Выравнивание». */
export const BLOCK_ALIGN_PANEL_TYPES: ReadonlySet<string> = new Set(
  [...ALIGN_SELF_APPLIED_BLOCK_TYPES, ...Object.keys(BLOCK_ALIGN_MODES)].filter(
    (type) => !ALIGN_OWN_CONTROL_BLOCK_TYPES.has(type),
  ),
);

/**
 * ПРИЗНАК, ОПИСАННЫЙ СЛОВАМИ, — а не «исторически так сложилось». Каждый тип
 * блока обязан быть либо в перечне выше, либо здесь, с причиной. Сторож
 * `__tests__/blockAlign.web1695.test.tsx` красит, если появился тип, который не
 * назван нигде: список правился поштучно трижды именно потому, что признака
 * вхождения не было записано.
 */
const NOT_APPLICABLE_GROUPS: ReadonlyArray<{
  reason: string;
  types: readonly string[];
}> = [
  {
    reason:
      'у типа есть СВОЁ поле выравнивания со своим смыслом — второе поле на то же свойство заводить нельзя',  // i18n-allow: признак для сторожа, автору не показывается
    types: ['gallery', 'image', 'columns_2'],
  },
  {
    reason:
      'занимает ширину колонки по назначению: кнопка, поле ввода, платёжная форма — двигать нечего, настройка была бы мёртвой',  // i18n-allow: признак для сторожа, автору не показывается
    types: [
      'button_next',
      'button_link',
      'button_to_pay',
      'close_webview_button',
      'return_to_app_button',
      'cancel_button',
      'resume_button',
      'restore_purchases',
      'stripe_checkout_form',
      'stripe_wallets_express',
      'email',
      'text_field',
      'name_field',
      'phone_input',
      'number_input',
      'date_picker',
      'select_dropdown',
      'slider_range',
      'toggle',
      'photo_upload',
      'plan_selector',
      'subscription_list',
      'downsell_offer',
      'price_block',
    ],
  },
  {
    reason:
      'служебный или невидимый блок: собственной ширины у него нет либо он рисуется поверх экрана целиком',  // i18n-allow: признак для сторожа, автору не показывается
    types: [
      'divider',
      'spacer',
      'spinner',
      'progress_bar',
      'progress_text',
      'conditional_branch',
      'redirect_url',
      'html',
      'cookie_consent',
      'downsell_popup',
      'loader_processing',
      'video_embed',
      'timer',
      'countdown',
      'result_screen',
    ],
  },
  {
    reason:
      'список вариантов ответа: строка варианта занимает колонку целиком, её положение задаётся оформлением варианта (WEB-1672/1674), а не положением блока',  // i18n-allow: признак для сторожа, автору не показывается
    types: [
      'single_choice',
      'multiple_choice',
      'multi_step_question',
      'likert_scale',
      'image_choice',
      'weight_picker',
      'height_picker',
      'statement_agreement',
      'preference_card',
    ],
  },
  {
    reason:
      'широкая карточка или схема со своим внутренним устройством — её содержимое расставлено собственной разметкой',  // i18n-allow: признак для сторожа, автору не показывается
    types: [
      'accordion',
      'before_after',
      'blur_teaser',
      'bmi_scale',
      'callout',
      'chart',
      'forecast_chart',
      'micro_feedback',
      'now_goal_bars',
      'payment_banners',
      'scratch_card',
      'urgency_banner',
    ],
  },
];

/**
 * Тип → причина, почему ОБЩЕГО выравнивания ему не положено. «Обложка» и
 * «Звёзды оценки» сюда не входят: у них выравнивание есть, просто своим
 * контролом (ALIGN_OWN_CONTROL_BLOCK_TYPES).
 */
export const ALIGN_NOT_APPLICABLE_REASONS: Readonly<Record<string, string>> =
  Object.fromEntries(
    NOT_APPLICABLE_GROUPS.flatMap((g) => g.types.map((t) => [t, g.reason])),
  );

/**
 * Умолчание = СЕГОДНЯШНЕЕ положение типа, чтобы уже собранные воронки не
 * сдвинулись. У заголовка и подзаголовка это центр (`--heading-align`), у
 * остальных — левый край.
 */
export function defaultAlignOf(blockType: string): BlockAlignValue {
  if (
    blockType === 'title' ||
    blockType === 'sub_title' ||
    blockType === 'payment_terms' ||
    // WEB-1822: новый блок, «сегодняшнего положения» у него нет — умолчание
    // берётся у ближайшего соседа по экрану оплаты (payment_terms): текст под
    // ценой стоит по центру.
    blockType === 'price_breakdown_text'
  ) {
    return 'center';
  }
  return 'left';
}

const JUSTIFY: Readonly<Record<BlockAlignValue, string>> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export interface BlockAlignFields {
  type: string;
  align?: BlockAlignValue;
  /**
   * Прежнее поле «Звёзд оценки» (WEB-691b). Читаются ОБА имени: в живых
   * воронках лежит оно, и его значение обязано продолжать работать — миграций
   * мы не делаем. Новое поведение при этом ровно то, что просил владелец:
   * подпись едет вместе со звёздами, а не остаётся на месте.
   */
  starsAlign?: BlockAlignValue;
}

/** Заданное автором выравнивание блока; undefined — автор его не трогал. */
export function resolveBlockAlign(
  fields: BlockAlignFields,
): BlockAlignValue | undefined {
  if (!(fields.type in BLOCK_ALIGN_MODES)) return undefined;
  if (fields.type === 'rating_stars') return fields.align ?? fields.starsAlign;
  return fields.align;
}

/** Стиль обёртки для режима типа. */
export function blockAlignStyle(
  blockType: string,
  align: BlockAlignValue,
): CSSProperties {
  return BLOCK_ALIGN_MODES[blockType] === 'content'
    ? { textAlign: align }
    : { display: 'flex', justifyContent: JUSTIFY[align] };
}

/**
 * Перегрузки — как у соседней обёртки `withBlockSpacing`: рендерер зовёт её на
 * `ReactElement | null` и обязан получить обратно тот же тип, иначе внешние
 * обёртки (подложка, поверхность) перестают типизироваться.
 */
export function withBlockAlign(
  fields: BlockAlignFields,
  node: React.ReactElement | null,
): React.ReactElement | null;
export function withBlockAlign(
  fields: BlockAlignFields,
  node: React.ReactNode,
): React.ReactNode;
export function withBlockAlign(
  fields: BlockAlignFields,
  node: React.ReactNode,
): React.ReactNode {
  const align = resolveBlockAlign(fields);
  if (!align) return node;
  if (node === null || node === undefined || node === false) return node;
  return (
    <div data-block-align={align} style={blockAlignStyle(fields.type, align)}>
      {node}
    </div>
  );
}
